<template>
  <div class="min-h-screen bg-gray-100">
    <!-- Demo Header -->
    <div class="bg-white shadow-sm border-b border-gray-200 p-4">
      <div class="max-w-7xl mx-auto">
        <h1 class="text-2xl font-bold text-gray-900">Welcome Email Builder</h1>
        <p class="text-gray-600 mt-1">Create professional welcome emails for your customers</p>
      </div>
    </div>

    <!-- Main Demo Container -->
    <div class="max-w-7xl mx-auto p-4">
      <!-- Template Management Bar -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <h2 class="text-lg font-semibold text-gray-900">Template Manager</h2>
            

          </div>

          <div class="flex items-center space-x-2">

            
            <button 
              @click="createNewTemplate"
              class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              New Template
            </button>
          </div>
        </div>

        <!-- Saved Templates List -->
        <div v-if="filteredTemplates.length > 0" class="mt-4">
          <h3 class="text-sm font-medium text-gray-700 mb-3">Saved Templates ({{ filteredTemplates.length }})</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div 
              v-for="template in filteredTemplates" 
              :key="template.id"
              class="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer"
              @click="loadTemplate(template)"
            >
              <div class="flex items-start justify-between mb-2">
                <h4 class="text-sm font-medium text-gray-900 truncate">{{ template.name }}</h4>
                <button 
                  @click.stop="deleteTemplate(template.id)"
                  class="text-gray-400 hover:text-red-500 transition-colors"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
              
              <div class="text-xs text-gray-500 mb-3">
                Created: {{ formatDate(template.createdAt) }}
              </div>
              
              <!-- Template Tags -->
              <div v-if="template.tags && template.tags.length > 0" class="flex flex-wrap gap-1">
                <span 
                  v-for="tag in template.tags" 
                  :key="tag"
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {{ tag }}
                </span>
              </div>
              <div v-else class="text-xs text-gray-400 italic">No tags</div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!showBuilder" class="text-center py-8">
          <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <h3 class="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
          <p class="text-gray-500 mb-4">Create your first email template to get started</p>
          <button 
            @click="createNewTemplate"
            class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
          >
            Create Template
          </button>
        </div>
      </div>



      <!-- Email Builder -->
      <div v-if="showBuilder" class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <AdvancedEmailBuilder
          :template="currentTemplate"
          :integration-mode="true"
          :show-close-button="true"
          @save="saveTemplate"
          @close="closeBuilder"
          @template-updated="handleTemplateUpdate"
        />
      </div>
    </div>

    <!-- Success Notification -->
    <div 
      v-if="showNotification" 
      class="fixed top-4 right-4 z-50 max-w-sm w-full bg-white border border-gray-200 rounded-lg shadow-lg"
    >
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="ml-3 w-0 flex-1">
            <p class="text-sm font-medium text-gray-900">{{ notificationTitle }}</p>
            <p class="mt-1 text-sm text-gray-500">{{ notificationMessage }}</p>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button @click="hideNotification" class="text-gray-400 hover:text-gray-600">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdvancedEmailBuilder from './AdvancedEmailBuilder.vue'


export default {
  name: 'EmailBuilderDemo',
  components: {
    AdvancedEmailBuilder
  },
  data() {
    return {
      showBuilder: false,
      currentTemplate: null,
      savedTemplates: [],
      filteredTemplates: [],
      
      // Notification system
      showNotification: false,
      notificationTitle: '',
      notificationMessage: ''
    }
  },

  methods: {
    createNewTemplate() {
      this.currentTemplate = null
      this.showBuilder = true
    },
    
    loadTemplate(template) {
      this.currentTemplate = template
      this.showBuilder = true
    },
    
    closeBuilder() {
      this.showBuilder = false
      this.currentTemplate = null
    },
    
    saveTemplate(templateData) {
      const template = {
        id: this.currentTemplate ? this.currentTemplate.id : this.generateId(),
        name: templateData.name,
        content: templateData.content,

        builderData: templateData.builderData,
        createdAt: this.currentTemplate ? this.currentTemplate.createdAt : new Date(),
        updatedAt: new Date()
      }
      
      if (this.currentTemplate) {
        // Update existing template
        const index = this.savedTemplates.findIndex(t => t.id === template.id)
        if (index !== -1) {
          this.savedTemplates.splice(index, 1, template)
        }
        this.showSuccess('Template Updated', `"${template.name}" has been updated successfully!`)
      } else {
        // Add new template
        this.savedTemplates.push(template)
        this.showSuccess('Template Saved', `"${template.name}" has been saved successfully!`)
      }
      
      this.updateFilteredTemplates()
      this.saveToLocalStorage()
      this.closeBuilder()
    },
    
    deleteTemplate(templateId) {
      if (confirm('Are you sure you want to delete this template?')) {
        const index = this.savedTemplates.findIndex(t => t.id === templateId)
        if (index !== -1) {
          const template = this.savedTemplates[index]
          this.savedTemplates.splice(index, 1)
          this.updateFilteredTemplates()
          this.saveToLocalStorage()
          this.showSuccess('Template Deleted', `"${template.name}" has been deleted.`)
        }
      }
    },
    
    handleTemplateUpdate(templateData) {
      // Handle real-time template updates if needed
      console.log('Template updated:', templateData)
    },
    
    updateFilteredTemplates() {
      this.filteredTemplates = [...this.savedTemplates]
    },
    
    generateId() {
      return Date.now().toString(36) + Math.random().toString(36).substr(2)
    },
    
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    showSuccess(title, message) {
      this.notificationTitle = title
      this.notificationMessage = message
      this.showNotification = true
      setTimeout(() => this.hideNotification(), 5000)
    },
    
    hideNotification() {
      this.showNotification = false
    },
    
    saveToLocalStorage() {
      localStorage.setItem('emailTemplates', JSON.stringify(this.savedTemplates))
    },
    
    loadFromLocalStorage() {
      const saved = localStorage.getItem('emailTemplates')
      if (saved) {
        try {
          this.savedTemplates = JSON.parse(saved)
          this.updateFilteredTemplates()
        } catch (error) {
          console.error('Error loading templates from localStorage:', error)
        }
      }
    }
  },
  
  mounted() {
    this.loadFromLocalStorage()
  }
}
</script>

<style scoped>
/* Custom scrollbars */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Smooth transitions */
* {
  transition: all 0.2s ease;
}

/* Hover effects */
.hover\:border-blue-300:hover {
  border-color: #93c5fd;
}
</style>
